import { formatMoney, formatDate } from "@/utils/format";
import { montantEnLettres, type ReceiptData } from "@/utils/receipt";
import type { SchoolInfo } from "@/types";
import { currencyLabel } from "@/utils/currency";

interface Props {
  data: ReceiptData;
  school: SchoolInfo;
}

export default function ReceiptSheet({ data, school }: Props) {
  return (
    <div className="print-sheet w-full overflow-hidden rounded-lg border border-background-200 bg-white text-foreground-900">
      {/* En-tête établissement */}
      <div className="flex flex-wrap items-start justify-between gap-4 border-b border-background-200 px-5 py-4">
        <div className="flex items-start gap-3">
          <div className="h-14 w-14 shrink-0 overflow-hidden rounded-md border border-background-200 bg-background-50">
            <img src={school.logo} alt={`Logo ${school.name}`} className="h-full w-full object-contain" />
          </div>
          <div>
            <h1 className="font-heading text-base font-bold uppercase tracking-wide text-primary-800">
              {school.name}
            </h1>
            <p className="text-[11px] text-foreground-600">{school.address}</p>
            <p className="text-[11px] text-foreground-600">{school.city}</p>
            <p className="text-[11px] text-foreground-600">
              {school.phone} • {school.email}
            </p>
          </div>
        </div>
        <div className="rounded-md border border-background-300 bg-background-50 px-3.5 py-2 text-right">
          <p className="text-[10px] uppercase tracking-wide text-foreground-500">N° du reçu</p>
          <p className="font-heading text-sm font-bold text-foreground-950">{data.receiptNo}</p>
          <p className="mt-1 text-[11px] text-foreground-500">Date : {formatDate(data.date)}</p>
          <p className="text-[11px] text-foreground-500">Année : {data.student.schoolYear}</p>
        </div>
      </div>

      {/* Titre */}
      <div className="bg-primary-600 px-5 py-2 text-center">
        <h2 className="font-heading text-xs font-semibold uppercase tracking-[0.25em] text-background-50">
          {data.title}
        </h2>
      </div>

      {data.notice && (
        <p className="mx-5 mt-3 rounded-md border border-secondary-300 bg-secondary-50 px-3 py-2 text-center text-xs font-semibold text-secondary-800">
          {data.notice}
        </p>
      )}

      {/* Informations élève */}
      <div className="grid grid-cols-1 gap-x-6 gap-y-2 border-b border-background-200 px-5 py-3.5 sm:grid-cols-2">
        <div className="flex items-center gap-2 text-sm">
          <span className="w-28 shrink-0 text-foreground-500">Élève</span>
          <span className="font-semibold text-foreground-950">{data.student.name}</span>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <span className="w-28 shrink-0 text-foreground-500">Matricule</span>
          <span className="text-foreground-800">{data.student.matricule}</span>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <span className="w-28 shrink-0 text-foreground-500">Classe</span>
          <span className="text-foreground-800">
            {data.student.className} — {data.student.cycleLabel}
          </span>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <span className="w-28 shrink-0 text-foreground-500">Mode de paiement</span>
          <span className="text-foreground-800">{data.method}</span>
        </div>
      </div>

      {/* Montant reçu */}
      <div className="mx-5 my-3.5 flex flex-wrap items-center justify-between gap-3 rounded-md border border-primary-200 bg-primary-50 px-4 py-3">
        <div>
          <p className="text-[11px] font-medium uppercase tracking-wide text-primary-700">Montant reçu</p>
          <p className="font-heading text-2xl font-bold text-primary-800">{formatMoney(data.amountReceived)}</p>
          <p className="mt-0.5 text-[11px] italic text-foreground-600">{montantEnLettres(data.amountReceived)}</p>
        </div>
        {data.note && (
          <p className="max-w-[220px] text-right text-[11px] text-foreground-600">Note : {data.note}</p>
        )}
      </div>

      {/* Situation des frais */}
      <div className="px-5">
        <table className="w-full border-collapse text-xs">
          <thead>
            <tr className="bg-background-100 text-left text-[11px] uppercase tracking-wide text-foreground-600">
              <th className="border border-background-200 px-3 py-2 font-semibold">Désignation</th>
              <th className="border border-background-200 px-3 py-2 text-right font-semibold">Montant dû</th>
              <th className="border border-background-200 px-3 py-2 text-right font-semibold">Payé</th>
              <th className="border border-background-200 px-3 py-2 text-right font-semibold">Reste</th>
            </tr>
          </thead>
          <tbody>
            {data.lines.map((line, index) => (
              <tr key={`${line.label}-${index}`} className={line.current ? "bg-primary-50/60" : ""}>
                <td className="border border-background-200 px-3 py-2 text-foreground-800">
                  <span className="flex items-center gap-1.5">
                    {line.current && <i className="ri-arrow-right-s-fill text-primary-600"></i>}
                    {line.label}
                  </span>
                </td>
                <td className="border border-background-200 px-3 py-2 text-right tabular-nums text-foreground-700">
                  {formatMoney(line.due)}
                </td>
                <td className="border border-background-200 px-3 py-2 text-right tabular-nums text-primary-700">
                  {formatMoney(line.paid)}
                </td>
                <td className="border border-background-200 px-3 py-2 text-right tabular-nums text-accent-700">
                  {formatMoney(line.remaining)}
                </td>
              </tr>
            ))}
            {data.lines.length === 0 && (
              <tr>
                <td colSpan={4} className="border border-background-200 px-3 py-4 text-center text-foreground-400">
                  Aucun frais enregistré.
                </td>
              </tr>
            )}
          </tbody>
          <tfoot>
            <tr className="bg-primary-100 font-semibold text-primary-900">
              <td className="border border-primary-300 px-3 py-2 uppercase tracking-wide">Total</td>
              <td className="border border-primary-300 px-3 py-2 text-right tabular-nums">{formatMoney(data.totalDue)}</td>
              <td className="border border-primary-300 px-3 py-2 text-right tabular-nums">{formatMoney(data.totalPaid)}</td>
              <td className="border border-primary-300 px-3 py-2 text-right tabular-nums">{formatMoney(data.remaining)}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Détail des versements */}
      {data.versements.length > 0 && (
        <div className="px-5 pt-4">
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-foreground-500">
            Détail des versements
          </p>
          <table className="w-full border-collapse text-xs">
            <thead>
              <tr className="bg-background-100 text-left text-[11px] uppercase tracking-wide text-foreground-600">
                <th className="border border-background-200 px-3 py-1.5 font-semibold">N° reçu</th>
                <th className="border border-background-200 px-3 py-1.5 font-semibold">Date</th>
                <th className="border border-background-200 px-3 py-1.5 font-semibold">Désignation</th>
                <th className="border border-background-200 px-3 py-1.5 font-semibold">Mode</th>
                <th className="border border-background-200 px-3 py-1.5 text-right font-semibold">Montant</th>
              </tr>
            </thead>
            <tbody>
              {data.versements.map((v) => (
                <tr key={v.receiptNo}>
                  <td className="border border-background-200 px-3 py-1.5 text-foreground-700">{v.receiptNo}</td>
                  <td className="border border-background-200 px-3 py-1.5 text-foreground-600">{formatDate(v.date)}</td>
                  <td className="border border-background-200 px-3 py-1.5 text-foreground-800">{v.label}</td>
                  <td className="border border-background-200 px-3 py-1.5 text-foreground-600">{v.method}</td>
                  <td className="border border-background-200 px-3 py-1.5 text-right tabular-nums text-foreground-900">
                    {formatMoney(v.amount)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Signatures */}
      <div className="mt-6 flex items-end justify-between gap-6 px-5 pb-5">
        <div className="w-44 border-t border-foreground-300 pt-1.5 text-center text-[11px] text-foreground-500">
          Le comptable
        </div>
        <div className="w-44 border-t border-foreground-300 pt-1.5 text-center text-[11px] text-foreground-500">
          Le parent / tuteur
        </div>
      </div>

      <div className="border-t border-background-200 bg-background-50 px-5 py-2 text-center text-[10px] text-foreground-500">
        Reçu établi en deux exemplaires : un pour l'établissement, un pour le parent. Devise : {currencyLabel()}.
      </div>
    </div>
  );
}